import {EVENT_DEFINITION_BY_CALLBACK, EVENT_DEFINITIONS, type CallbackName} from './eventRegistry';
import type {AdvisorError, AdvisorOptions, EventContext, PublicEvent} from './types';

/** Compatibility export; derived from the canonical event registry. */
export const eventNames = Object.freeze(Object.fromEntries(
  EVENT_DEFINITIONS.map(definition => [definition.callbackName, definition.eventName]),
)) as Record<CallbackName, PublicEvent['eventName']>;

export function safeError(error: unknown, code = 'HOST_ACTION_FAILED'): AdvisorError {
  if (error && typeof error === 'object' && 'code' in error && 'message' in error) {
    const candidate = error as Partial<AdvisorError>;
    return {
      code: typeof candidate.code === 'string' ? candidate.code : code,
      message: typeof candidate.message === 'string' ? candidate.message : String(candidate.message),
      ...(typeof candidate.detail === 'string' && {detail: candidate.detail}),
      ...(candidate.stage && {stage: candidate.stage}),
      ...(typeof candidate.recoverable === 'boolean' && {recoverable: candidate.recoverable}),
    };
  }
  return {code, message: error instanceof Error ? error.message : String(error)};
}

function contextualData(data: Record<string, unknown>, context: EventContext): Record<string, unknown> {
  const keys: (keyof EventContext)[] = ['originalPrompt', 'source', 'interaction', 'groupTitle', 'groupId', 'position', 'reason'];
  return keys.reduce<Record<string, unknown>>((result, key) => {
    const value = context[key];
    if (value !== undefined && result[key] === undefined) result[key] = value;
    return result;
  }, {...data});
}

/**
 * Dispatches one canonical envelope to the named callback and then to onEvent.
 * Host callback failures are deliberately isolated and become non-recursive
 * onError events so neither the agent nor the catch-all stream is interrupted.
 */
export class EventBus {
  context: EventContext = {};
  displayed = new Set<string>();
  active = true;

  constructor(readonly options: AdvisorOptions) {}

  private invoke(callback: unknown, event: PublicEvent, onFailure?: (error: unknown) => void) {
    if (typeof callback !== 'function') return;
    const fail = (error: unknown) => {
      this.warn(error);
      onFailure?.(error);
    };
    try {
      const result = callback(event);
      if (result && typeof (result as Promise<unknown>).catch === 'function') {
        void (result as Promise<unknown>).catch(fail);
      }
    } catch (error) {
      fail(error);
    }
  }

  private warn(error: unknown) {
    if (this.options.config?.debug) console.warn('[Drinks Advisor] Host callback failed; isolated.', error);
  }

  emit(name: CallbackName, data: Record<string, unknown> = {}, context: EventContext = {}) {
    if (!this.active) return;
    const timestamp = new Date().toISOString();
    const mergedContext = {...this.context, ...context, timestamp};
    const definition = EVENT_DEFINITION_BY_CALLBACK[name];
    const event: PublicEvent = {
      eventName: definition.eventName,
      timestamp,
      ...(mergedContext.sessionId && {sessionId: mergedContext.sessionId}),
      ...(mergedContext.threadId && {threadId: mergedContext.threadId}),
      ...(mergedContext.intentResultId && {intentResultId: mergedContext.intentResultId}),
      ...(mergedContext.searchResultId && {searchResultId: mergedContext.searchResultId}),
      data: contextualData(data, mergedContext),
    };

    this.invoke(
      this.options.events?.[name],
      event,
      name === 'onError' ? undefined : error => this.error(error, mergedContext, 'HOST_CALLBACK_FAILED', 'host_callback', true),
    );
    this.invoke(this.options.onEvent, event);
  }

  error(
    error: unknown,
    context: EventContext = {},
    code = 'HOST_ACTION_FAILED',
    stage: NonNullable<AdvisorError['stage']> = 'host_callback',
    recoverable = true,
  ) {
    const info = safeError(error, code);
    this.emit('onError', {
      error: {...info, stage: info.stage || stage, recoverable: info.recoverable ?? recoverable},
      errorCode: info.code,
      errorMessage: info.message,
      stage: info.stage || stage,
      recoverable: info.recoverable ?? recoverable,
    }, context);
  }

  once(key: string, action: () => void) {
    if (this.displayed.has(key)) return;
    this.displayed.add(key);
    action();
  }
}
